/* eslint-disable max-lines */
import type { PropsWithChildren } from "react";
import { atom, useAtomValue } from "jotai";
import { useHydrateAtoms } from "jotai/utils";

import { DEFAULT_FREE_SHIPPING_THRESHOLD } from "../constants/constants";

// Session-level override for pin-based redraw. When false, disables pin-based redraw
// NOTE: It resets on page reload.
export const pinBasedRedrawOverrideAtom = atom<boolean | null>(null);

export type DesignLabConfig = {
  flip?: {
    paidExpertEditAddon?: boolean;
    madeSamplesV1?: boolean;
    designLabCommunityArt?: boolean;
    cachedDeliveryDateAvailable?: boolean;
    manualBgRemovalBrushSize?: boolean;
    weaveAiImageRefinement?: boolean;
    editToolsAiImageRefinement?: boolean;
    dtfWorkerEnhancement?: boolean;
    dtfBackendTriggeredEnhancement?: boolean;
    designLabNewEnhancementTrigger?: boolean;
    customerReviewOnPrintRedraws?: boolean;
    customerReviewOnRedraws?: boolean;
    pinBasedRedrawEnabled?: boolean;
    designLabPinSessionRecording?: boolean;
  };
  experiments?: {
    abShippingEnhancementServices?: number;
    abDesignLabReorderTabs?: number;
    abPinBasedRedrawNotes?: number;
    abPinBasedRedrawNotesV2?: number;
    abExitModalDesignLab?: number;
  };
  is_sample?: boolean;
  isSample?: boolean;
  ai_design?: boolean;
  aiDesign?: boolean;
  aiCreditsBalance?: number;
  cachedEstimatedDeliveryDate?: string;
  orderNumber?: string;
  fbData?: string;
  supportPhoneNumber: string;
  defaultCompositionId: string;
  isStaff?: boolean;
  isShopifyRecipeCreator?: boolean;
  editingRecipeId?: string;
  editingStoreId?: string;
  freeShippingThreshold?: number;
  isFreeShippingThresholdZero?: boolean;
  printReseller?: boolean;
};

export type ConfigProviderProps = {
  config: DesignLabConfig;
};

type DesignLabComputedConfig = ReturnType<typeof computeDesignLabConfig>;

const designLabConfigAtom = atom<DesignLabConfig | null>(null);

function computeDesignLabConfig(config: DesignLabConfig) {
  const shouldShowArtistEditAddon = config.flip?.paidExpertEditAddon;
  const shouldShowExpertTab = !config.flip?.paidExpertEditAddon;

  const shouldExperiments = config.experiments;

  // Support both snake_case and camelCase versions
  const isSample = config.isSample || config.is_sample || false;
  const aiDesign = config.aiDesign || config.ai_design || false;
  const isStaff = config.isStaff || false;
  const isShopifyRecipeCreator = config.isShopifyRecipeCreator ?? false;
  const editingRecipeId = config.editingRecipeId ?? null;
  const editingStoreId = config.editingStoreId ?? null;

  const shouldShowCommunityArt = config.flip?.designLabCommunityArt || false;
  const shouldShowCachedDeliveryDate = config.flip?.cachedDeliveryDateAvailable || false;
  const { cachedEstimatedDeliveryDate } = config;
  const shouldShowManualBgRemovalBrushSize = config.flip?.manualBgRemovalBrushSize || false;

  const flip = (config.flip ?? {}) as Record<string, boolean>;
  const shouldShowWeaveAiImageRefinement = flip["weaveAiImageRefinement"] ?? false;
  const shouldShowEditToolsAiImageRefinement = flip["editToolsAiImageRefinement"] ?? false;
  const { supportPhoneNumber, defaultCompositionId } = config;
  const dtfWorkerEnhancement = flip["dtfWorkerEnhancement"] ?? false;
  const dtfBackendTriggeredEnhancement = flip["dtfBackendTriggeredEnhancement"] ?? false;
  const shouldUseDesignLabNewEnhancementTrigger = flip["designLabNewEnhancementTrigger"] ?? false;
  const shouldShowCustomerReviewOnPrintRedraws = flip["customerReviewOnPrintRedraws"] ?? false;
  const shouldShowCustomerReviewOnRedraws = flip["customerReviewOnRedraws"] ?? false;
  const freeShippingThreshold = config.freeShippingThreshold ?? DEFAULT_FREE_SHIPPING_THRESHOLD;
  const isFreeShippingThresholdZero = config.isFreeShippingThresholdZero ?? false;
  const printReseller = config.printReseller ?? false;
  const pinBasedRedrawEnabled = flip["pinBasedRedrawEnabled"] ?? false;
  const designLabPinSessionRecording = flip["designLabPinSessionRecording"] ?? false;

  return {
    config,
    shouldShowArtistEditAddon,
    shouldShowExpertTab,
    shouldExperiments,
    isSample,
    aiDesign,
    isStaff,
    isShopifyRecipeCreator,
    editingRecipeId,
    editingStoreId,
    shouldShowCommunityArt,
    shouldShowCachedDeliveryDate,
    cachedEstimatedDeliveryDate,
    shouldShowManualBgRemovalBrushSize,
    shouldShowWeaveAiImageRefinement,
    shouldShowEditToolsAiImageRefinement,
    supportPhoneNumber,
    defaultCompositionId,
    dtfWorkerEnhancement,
    dtfBackendTriggeredEnhancement,
    shouldUseDesignLabNewEnhancementTrigger,
    shouldShowCustomerReviewOnPrintRedraws,
    shouldShowCustomerReviewOnRedraws,
    freeShippingThreshold,
    isFreeShippingThresholdZero,
    printReseller,
    pinBasedRedrawEnabled,
    designLabPinSessionRecording
  };
}

const designLabComputedConfigAtom = atom<DesignLabComputedConfig | null>(get => {
  const config = get(designLabConfigAtom);
  if (!config) return null;
  return computeDesignLabConfig(config);
});

function useDesignLabComputedConfig() {
  const computed = useAtomValue(designLabComputedConfigAtom);
  if (!computed) {
    throw new Error("ConfigProvider is missing");
  }
  return computed;
}

export function ConfigProvider({ config, children }: PropsWithChildren<ConfigProviderProps>) {
  useHydrateAtoms([[designLabConfigAtom, config]]);
  return <>{children}</>;
}

export function useDesignLabConfig() {
  return useDesignLabComputedConfig().config;
}

export function useDtfBackendTriggeredEnhancement() {
  return useDesignLabComputedConfig().dtfBackendTriggeredEnhancement;
}

export function useDtfWorkerEnhancement() {
  return useDesignLabComputedConfig().dtfWorkerEnhancement;
}

export function useShowArtistEditAddon() {
  return useDesignLabComputedConfig().shouldShowArtistEditAddon;
}

export function useShowExpertTab() {
  return useDesignLabComputedConfig().shouldShowExpertTab;
}
export function useExperiments() {
  return useDesignLabComputedConfig().shouldExperiments;
}

export function useIsSample() {
  return useDesignLabComputedConfig().isSample;
}

export function useAiDesign() {
  return useDesignLabComputedConfig().aiDesign;
}

export function useIsStaff() {
  return useDesignLabComputedConfig().isStaff;
}

export function useIsShopifyRecipeCreator() {
  return useDesignLabComputedConfig().isShopifyRecipeCreator;
}

export function useEditingRecipeId() {
  return useDesignLabComputedConfig().editingRecipeId;
}

export function useEditingStoreId() {
  return useDesignLabComputedConfig().editingStoreId;
}

export function useShowCommunityArt() {
  return useDesignLabComputedConfig().shouldShowCommunityArt;
}

export function useShowCachedDeliveryDate() {
  return useDesignLabComputedConfig().shouldShowCachedDeliveryDate;
}

export function useCachedEstimatedDeliveryDate() {
  return useDesignLabComputedConfig().cachedEstimatedDeliveryDate;
}

export function useShowManualBgRemovalBrushSize() {
  return useDesignLabComputedConfig().shouldShowManualBgRemovalBrushSize;
}

export function useShowWeaveAiImageRefinement() {
  return useDesignLabComputedConfig().shouldShowWeaveAiImageRefinement;
}

export function useShowEditToolsAiImageRefinement() {
  return useDesignLabComputedConfig().shouldShowEditToolsAiImageRefinement;
}

export function useSupportPhoneNumber() {
  return useDesignLabComputedConfig().supportPhoneNumber;
}

export function useDefaultCompositionId() {
  return useDesignLabComputedConfig().defaultCompositionId;
}

export function useDesignLabNewEnhancementTrigger() {
  return useDesignLabComputedConfig().shouldUseDesignLabNewEnhancementTrigger;
}

export function useFreeShippingThreshold() {
  return useDesignLabComputedConfig().freeShippingThreshold;
}

export function useIsFreeShippingThresholdZero() {
  return useDesignLabComputedConfig().isFreeShippingThresholdZero;
}

export function usePrintReseller() {
  return useDesignLabComputedConfig().printReseller;
}

export function usePinBasedRedrawEnabled() {
  const override = useAtomValue(pinBasedRedrawOverrideAtom);
  const config = useDesignLabComputedConfig();
  if (override === false) return false;
  return config.pinBasedRedrawEnabled;
}

export function useDesignLabPinSessionRecording() {
  return useDesignLabComputedConfig().designLabPinSessionRecording;
}
